import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    TextField,
    MenuItem,
    RadioGroup,
    FormControlLabel,
    Radio,
} from "@mui/material";
import { colors } from "../../colors"; // Import your color constants
import axiosInstance from "../../axiosConfig";

const MobileSurveyLayout = ({ surveyId, participantId }) => {
    const [surveyData, setSurveyData] = useState(null); // Welcome screen data
    const [currentSection, setCurrentSection] = useState(null); // Current section
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0); // Current question index
    const [responses, setResponses] = useState({}); // Store user responses
    const [loading, setLoading] = useState(true); // Loading state for welcome API
    const [sectionLoading, setSectionLoading] = useState(false); // Loading state for section API
    const [error, setError] = useState(null); // Error state

    // Fetch the welcome data for the survey
    useEffect(() => {
        const fetchSurveyData = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/mobile/surveys/${surveyId}/welcome`);
                setSurveyData(response.data);
            } catch (err) {
                setError(err.message || "Error fetching survey data.");
            } finally {
                setLoading(false);
            }
        };

        fetchSurveyData();
    }, [surveyId]);

    // Start the survey or fetch the first incomplete section
    const handleStartSurvey = async () => {
        setSectionLoading(true);
        try {
            const response = await axiosInstance.get(`/mobile/surveys/${surveyId}/${participantId}`);
            const section = response.data.sections.find((s) => !s.completed); // Get first incomplete section
            setCurrentSection(section);
            setCurrentQuestionIndex(0); // Start with the first question
            setResponses({}); // Reset responses
        } catch (err) {
            setError(err.message || "Error fetching the first section.");
        } finally {
            setSectionLoading(false);
        }
    };

    // Handle response change for the current question
    const handleResponseChange = (questionId, value) => {
        setResponses((prev) => ({
            ...prev,
            [questionId]: value,
        }));
    };

    // Handle "Next" button click
    const handleNextQuestion = () => {
        if (currentQuestionIndex < currentSection.questions.length - 1) {
            setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
        } else {
            handleSubmitSection(); // Submit the section if the last question is answered
        }
    };

    const handleSubmitSection = async () => {
        try {
            // Submit the current section's responses
            const response = await axiosInstance.post(`/mobile/surveys/${surveyId}/${participantId}/submit`, {
                sectionIndex: currentSection.sectionIndex,
                responses,
            });

            // Use the response to fetch the next incomplete section
            const nextSection = response.data.sections.find((section) => !section.completed);

            if (nextSection) {
                // Update the state with the next incomplete section
                setCurrentSection(nextSection);
                setCurrentQuestionIndex(0); // Reset to the first question
                setResponses({}); // Clear responses for the new section
            } else {
                // Handle survey completion if no incomplete sections exist
                alert("Survey completed successfully!");
                setCurrentSection(null); // Clear the current section
            }
        } catch (err) {
            setError(err.message || "Error submitting the section.");
        }
    };



    // Render each question based on its type
    const renderQuestion = (question) => {
        return (
            <Box>

                {(() => {
                    switch (question.questionType) {
                        case "QUOTE":
                            return (
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontStyle: "italic",
                                        fontWeight: "bold",
                                        borderLeft: `4px solid ${colors.racineRedDark}`,
                                        paddingLeft: 2,
                                        mb: 2,
                                        color: colors.primaryApplicationText,
                                    }}
                                >
                                    {question.questionText}
                                </Typography>
                            );

                        case "INPUT":
                            return (
                                <Box sx={{ mb: 0 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: "bold",
                                            color: colors.primaryApplicationText,
                                        }}
                                    >
                                        {question.questionText || "Question text is missing."}
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Type your response here"
                                        onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
                                        value={responses[question.questionId] || ""}
                                        sx={{ mb: 2 }}
                                    />
                                </Box>
                            );


                        case "TABLE":
                            return (
                                <Box sx={{ mb: 0 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: "bold",
                                            color: colors.primaryApplicationText,
                                        }}
                                    >
                                        {question.questionText || "Question text is missing."}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 2, color: colors.primaryApplicationText }}>

                                    </Typography>
                                </Box>
                            );

                        case "MCQ":
                            return (
                                <Box sx={{ mb: 0 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: "bold",
                                            color: colors.primaryApplicationText,
                                        }}
                                    >
                                        {question.questionText || "Question text is missing."}
                                    </Typography>
                                    <RadioGroup
                                        value={responses[question.questionId] || ""}
                                        onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
                                        sx={{ mb: 2 }}
                                    >
                                        {question.options.map((option, index) => (
                                            <FormControlLabel
                                                key={index}
                                                value={option}
                                                control={<Radio />}
                                                label={option}
                                                sx={{ mb: 1 }}
                                            />
                                        ))}
                                    </RadioGroup>
                                </Box>
                            );

                        case "SLIDE":
                        case "SCQ":
                            return (
                                <Box sx={{ mb: 0 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: "bold",
                                            color: colors.primaryApplicationText,
                                        }}
                                    >
                                        {question.questionText || "Question text is missing."}
                                    </Typography>
                                    <RadioGroup
                                        value={responses[question.questionId] || ""}
                                        onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
                                        sx={{ mb: 2 }}
                                    >
                                        {question.options.map((option, index) => (
                                            <FormControlLabel
                                                key={index}
                                                value={option}
                                                control={<Radio />}
                                                label={option}
                                                sx={{ mb: 1 }}
                                            />
                                        ))}
                                    </RadioGroup>
                                </Box>
                            );

                        case "DROPDOWN":
                            return (
                                <Box sx={{ mb: 0 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mb: 2,
                                            fontWeight: "bold",
                                            color: colors.primaryApplicationText,
                                        }}
                                    >
                                        {question.questionText || "Question text is missing."}
                                    </Typography>
                                    <TextField
                                        select
                                        fullWidth
                                        variant="outlined"
                                        value={responses[question.questionId] || ""}
                                        onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
                                        sx={{ mb: 2 }}
                                    >
                                        {question.options.map((option, index) => (
                                            <MenuItem key={index} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            );

                        default:
                            return (
                                <Typography
                                    variant="body1"
                                    sx={{
                                        mb: 2,
                                        color: colors.primaryApplicationText,
                                        fontStyle: "italic",
                                    }}
                                >
                                    Unsupported question type: {question.questionType}
                                </Typography>
                            );
                    }
                })()}
            </Box>
        );
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    backgroundColor: "#FFF8E1",
                }}
            >
                <CircularProgress sx={{ color: colors.racineRedDark }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    padding: "20px",
                    backgroundColor: "#FFF8E1",
                    textAlign: "center",
                }}
            >
                <Typography variant="h6" color="error">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                height: "100vh",
            }}
        >
            {!currentSection ? (
                //Welcome Screen comes here!
                <>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100vh", // Full viewport height
                            width: "100%",
                            alignItems: "center",
                            backgroundColor: "#F2EFEA", // Soft, neutral background color
                        }}
                    >
                        {/* Header 1: Logo */}
                        <Box
                            sx={{
                                flex: "0 0 10%", // 10% height for the logo
                                width: "100%",
                                textAlign: "center",
                                py: 1,
                            }}
                        >
                            <Box
                                component="img"
                                src={`${process.env.PUBLIC_URL}/racine.png`} // Replace with your actual image file
                                alt="Racine Logo"
                                sx={{
                                    height: "50px", // Adjusted height for logo
                                    objectFit: "contain",
                                    mx: "auto",
                                }}
                            />
                        </Box>

                        {/* Separator */}
                        <Box
                            sx={{
                                height: "0",
                                width: "100%",
                                borderTop: "1px dashed #1E2742", // Dashed line for visual separation
                                margin: "0",
                            }}
                        />

                        {/* Banner */}
                        <Box
                            sx={{
                                flex: "0 0 6%", // 6% height for the banner
                                width: "100%",
                                backgroundColor: colors.primaryBannerBlue,
                                color: "white",
                                textAlign: "center",
                                fontSize: { xs: "0.9rem", sm: "1.1rem" },
                                fontWeight: "500",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            Your opinion matters!
                        </Box>

                        {/* Header 2: Survey Name */}
                        <Box
                            sx={{
                                flex: "0 0 8%", // 8% height for the survey name
                                width: "100%",
                                textAlign: "center",
                                py: 1,
                                backgroundColor: "#F4EFE9", // Light contrasting background
                                borderBottom: "1px solid #E0E0E0", // Subtle border
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    color: colors.primaryApplicationText,
                                    fontWeight: "bold",
                                }}
                            >
                                {surveyData?.surveyName || "Welcome to Our Survey"}
                            </Typography>
                        </Box>

                        {/* Middle Content: Welcome Message */}
                        <Box
                            sx={{
                                flex: "1 1 auto", // Remaining space for the middle section
                                width: "90%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                textAlign: "center",
                                backgroundColor: "#F4EFE9",
                                border: "1px solid #E0E0E0", // Subtle border for definition
                                borderRadius: "12px", // Rounded corners
                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)", // Subtle shadow
                                px: 4,
                                py: 5,
                                mt: 3,
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: "bold",
                                    mb: 2,
                                    color: colors.primaryBannerBlue,
                                }}
                            >
                                Welcome!
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: colors.primaryApplicationText,
                                    mb: 3,
                                    fontSize: { xs: "0.9rem", sm: "1rem" },
                                }}
                            >
                                We value your insights and feedback. This survey will take just a few minutes and will help us improve.
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#9E9E9E", // Muted text color for additional details
                                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                                }}
                            >
                                Your responses are completely confidential and appreciated.
                            </Typography>
                        </Box>

                        {/* Footer */}
                        <Box
                            sx={{
                                flex: "0 0 10%", // 10% height for the footer
                                width: "90%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 0,
                                py: 2,
                            }}
                        >
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleStartSurvey}
                                disabled={sectionLoading}
                                sx={{
                                    backgroundColor: colors.racineRedDark,
                                    color: "#fff",
                                    padding: "12px",
                                    fontSize: "1rem",
                                    fontWeight: "bold",
                                    "&:hover": {
                                        backgroundColor: colors.racineRedLight,
                                    },
                                }}
                            >
                                {sectionLoading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Start Survey"}
                            </Button>
                        </Box>
                    </Box>
                </>
            ) : (
                // Survey comes here
                <>
                    {/* Mobile Body */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100vh", // Full viewport height
                            width: "100%",
                            alignItems: "center",
                            backgroundColor: "#F5F5F5", // Background color for the entire page
                        }}
                    >
                        {/* Header 1: Logo */}
                        <Box
                            sx={{
                                flex: "0 0 10%", // 10% height for the logo
                                width: "100%",
                                textAlign: "center",
                                py: 1,
                            }}
                        >
                            <Box
                                component="img"
                                src={`${process.env.PUBLIC_URL}/racine.png`} // Replace with your actual image file
                                alt="Racine Logo"
                                sx={{
                                    height: "50px",
                                    objectFit: "contain",
                                    mx: "auto",
                                }}
                            />
                        </Box>
                        {/* Separator */}
                        <Box
                            sx={{
                                height: '0',
                                width: '100%',
                                borderTop: '1px dashed #1E2742', // Dashed line
                                margin: '0',
                            }}
                        />

                        {/* Banner */}
                        <Box
                            sx={{
                                flex: "0 0 5%", // 5% height for the banner
                                width: "100%",
                                backgroundColor: colors.primaryBannerBlue,
                                color: 'white',
                                textAlign: 'center',
                                fontSize: { xs: '1rem', sm: '1.2rem' },
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            Your opinion matters!
                        </Box>

                        {/* Header 2: Survey Name */}
                        <Box
                            sx={{
                                flex: "0 0 7%", // 7% height for the survey name
                                width: "100%",
                                textAlign: "center",
                                py: 1,
                                backgroundColor: "#F5F5F5",
                                borderBottom: "1px solid #E0E0E0",
                            }}
                        >
                            <Typography
                                variant="h7"
                                sx={{
                                    color: colors.primaryApplicationText,
                                    fontWeight: "bold",
                                }}
                            >
                                {surveyData?.surveyName || "Survey Name"}
                            </Typography>
                        </Box>

                        {/* Part 3: Section Name */}
                        <Box
                            sx={{
                                flex: "0 0 8%", // 8% height for the section name
                                width: "100%",
                                textAlign: "center",
                                py: 1,
                                backgroundColor: "#FFFFFF",
                                borderBottom: "1px solid #E0E0E0",
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: colors.primaryApplicationText,
                                    fontWeight: "bold",
                                }}
                            >
                                {currentSection?.sectionTitle || "Section Title"}
                            </Typography>
                        </Box>

                        {/* Survey Questions and Responses */}
                        <Box
                            sx={{
                                flex: "1 1 auto", // Remaining space for questions
                                width: "90%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                backgroundColor: "#FFFFFF",
                                border: `1.5px solid ${colors.racineRedLight}`,
                                borderRadius: "12px",
                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                                textAlign: "center",
                                px: 2,
                                py: 2,
                                mt: 1,
                            }}
                        >
                            {currentSection && renderQuestion(currentSection.questions[currentQuestionIndex])}
                        </Box>

                        {/* Footer */}
                        <Box
                            sx={{
                                flex: "0 0 10%", // 10% height for the footer
                                width: "100%",
                                minHeight: " 60px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 0,
                                py: 2,
                                backgroundColor: "#FFFFFF",
                            }}
                        >
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleNextQuestion}
                                sx={{
                                    backgroundColor: colors.racineRedDark,
                                    color: "#fff",
                                    fontSize: "1rem",
                                    fontWeight: "bold",
                                    width: "90%",
                                }}
                            >
                                {currentQuestionIndex === currentSection?.questions?.length - 1 ? "Submit" : "Next"}
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default MobileSurveyLayout;
